import React, { useState } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { ShoppingCart, User, Heart, Menu, X, LogOut, Shield } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Layout = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-concrete-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 glass border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <span className="font-anton text-2xl tracking-tight text-deep-obsidian">VINTAGE</span>
              <span className="font-bebas text-2xl text-chicago-red">KICKS</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <Link 
                to="/products" 
                className="uppercase font-bold tracking-widest text-xs text-deep-obsidian hover:text-chicago-red transition-colors"
                data-testid="nav-products"
              >
                Shop
              </Link>
              <Link 
                to="/products?era=1990s" 
                className="uppercase font-bold tracking-widest text-xs text-deep-obsidian hover:text-chicago-red transition-colors"
              >
                90s Era
              </Link>
              <Link 
                to="/products?era=2000s" 
                className="uppercase font-bold tracking-widest text-xs text-deep-obsidian hover:text-chicago-red transition-colors"
              >
                2000s Era
              </Link>
              <Link 
                to="/size-guide" 
                className="uppercase font-bold tracking-widest text-xs text-deep-obsidian hover:text-chicago-red transition-colors"
              >
                Size Guide
              </Link>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <>
                  <Link 
                    to="/wishlist" 
                    className="p-2 hover:bg-black/5 rounded-sm transition-colors"
                    data-testid="nav-wishlist"
                  >
                    <Heart className="w-5 h-5" />
                  </Link>
                  <Link 
                    to="/cart" 
                    className="p-2 hover:bg-black/5 rounded-sm transition-colors relative"
                    data-testid="nav-cart"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {cartCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-chicago-red text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {cartCount}
                      </span>
                    )}
                  </Link>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="rounded-sm" data-testid="user-menu">
                        <User className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <div className="px-2 py-1.5 text-sm font-medium">{user?.name}</div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/profile" className="cursor-pointer" data-testid="menu-profile">
                          <User className="w-4 h-4 mr-2" />
                          Profile
                        </Link>
                      </DropdownMenuItem>
                      {user?.is_admin && (
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="cursor-pointer" data-testid="menu-admin">
                            <Shield className="w-4 h-4 mr-2" />
                            Admin Panel
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="menu-logout">
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="hidden md:flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" className="uppercase tracking-widest text-xs font-bold" data-testid="nav-login">
                      Login
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button className="bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-xs font-bold rounded-sm" data-testid="nav-signup">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 hover:bg-black/5 rounded-sm"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-black/5">
            <div className="px-6 py-4 space-y-4">
              <Link 
                to="/products" 
                className="block uppercase font-bold tracking-widest text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Shop All
              </Link>
              <Link 
                to="/products?era=1990s" 
                className="block uppercase font-bold tracking-widest text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                90s Era
              </Link>
              <Link 
                to="/products?era=2000s" 
                className="block uppercase font-bold tracking-widest text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                2000s Era
              </Link>
              <Link 
                to="/size-guide" 
                className="block uppercase font-bold tracking-widest text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Size Guide
              </Link>
              {!isAuthenticated && (
                <div className="pt-4 border-t border-black/10 flex gap-4">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="uppercase tracking-widest text-xs font-bold">
                      Login
                    </Button>
                  </Link>
                  <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-xs font-bold">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="pt-16">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-deep-obsidian text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <span className="font-anton text-3xl tracking-tight">VINTAGE</span>
                <span className="font-bebas text-3xl text-chicago-red">KICKS</span>
              </div>
              <p className="text-cement-grey font-chivo text-sm max-w-md">
                Curating the finest original basketball shoes from the 90s and 2000s. 
                Every pair tells a story of basketball history.
              </p>
            </div>
            <div>
              <h4 className="font-bebas text-xl mb-4">SHOP</h4>
              <ul className="space-y-2 text-cement-grey text-sm">
                <li><Link to="/products" className="hover:text-white transition-colors">All Products</Link></li>
                <li><Link to="/products?era=1990s" className="hover:text-white transition-colors">90s Era</Link></li>
                <li><Link to="/products?era=2000s" className="hover:text-white transition-colors">2000s Era</Link></li>
                <li><Link to="/products?category=Jordan" className="hover:text-white transition-colors">Jordan</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bebas text-xl mb-4">HELP</h4>
              <ul className="space-y-2 text-cement-grey text-sm">
                <li><Link to="/size-guide" className="hover:text-white transition-colors">Size Guide</Link></li>
                <li><span className="hover:text-white transition-colors cursor-pointer">Shipping</span></li>
                <li><span className="hover:text-white transition-colors cursor-pointer">Returns</span></li>
                <li><span className="hover:text-white transition-colors cursor-pointer">Contact</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-12 pt-8 text-center text-cement-grey text-sm">
            <p>&copy; 2024 Vintage Kicks. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
